import type {
  DepartureRowDto,
  DepartureTourDto,
} from "../dtos/traveler/departures.dto";
import type {
  TourPageWhatsappContactView,
  TourPageWhatsappView,
} from "../tour-page/view";
import type { MoneyDto } from "../value-objects/money.schema";
import {
  DEFAULT_DEPARTURE_SORT,
  DEPARTURES_PAGE_SIZE,
} from "../dtos/traveler/departures.dto";
import type {
  DepartureDatesSettings,
  DepartureFilters,
} from "./departure-dates-block";
import { tourCardPath } from "./tour-cards";

/**
 * The Departure Dates block's stored parts (docs/features/departure-dates,
 * design.md D104), and the pieces of data each one is drawn from.
 *
 *   section .tc-departures                 the block; class-only
 *   ├── form .tc-departures__filters       GET, this page; role="search"
 *   │   ├── tc-combobox  [filter=tour]     multiple, name="tour"
 *   │   │   └── departure-tour-options     the tours, from data
 *   │   ├── tc-radio-group [filter=year]
 *   │   │   └── departure-choices of=year  the years, from data
 *   │   ├── tc-radio-group [filter=month]  "All", then
 *   │   │   └── departure-choices of=month the months, from data
 *   │   ├── tc-radio-group [filter=sort]   three stored options
 *   │   └── button .tc-departures__apply   for a page with no script
 *   └── departure-list                     every setting; the stored card
 *
 * That is the "stack" filter bar. The other four arrange the same controls
 * in rows, and draw Sort by (and in Compact the year and month) as a
 * single-choice `tc-combobox`; a control is always found by its
 * `data-departure-filter`, never by its place.
 *
 * Every part is stored, so the operator styles and words it, except what the
 * data decides: which tours, years and months there are, and the rows. Those
 * are the three data nodes, filled by `bindDepartureDates`.
 */

/** The filter form: GET, to this page. Its own type, so the editor can hold
 *  a submit in the canvas and the storefront draws a real `<form>`. */
export const DEPARTURE_FILTERS_TYPE = "departure-filters";

/** The list: every setting of the block (`departureDatesSettingsSchema`),
 *  and the stored card. */
export const DEPARTURE_LIST_TYPE = "departure-list";

/**
 * The "168 departures" line, as its own stored part: selectable and
 * styleable. The bind writes the total on it. A list stored with
 * `DEPARTURE_COUNT_PART_ATTR` leaves the line to this node; a list stored
 * before it existed draws the line itself.
 */
export const DEPARTURE_COUNT_TYPE = "departure-count";
export const DEPARTURE_COUNT_PART_ATTR = "data-count-part";

/**
 * The foot under the list, the same way: a stored box holding Show more and
 * the "Showing 10 of 168" line, each its own part. A list stored with
 * `DEPARTURE_FOOT_PART_ATTR` leaves the foot to them; a list stored before
 * draws its own.
 */
export const DEPARTURE_MORE_TYPE = "departure-more";
export const DEPARTURE_SHOWN_TYPE = "departure-shown";
export const DEPARTURE_FOOT_PART_ATTR = "data-foot-part";

/** The tour picker's options. */
export const DEPARTURE_TOUR_OPTIONS_TYPE = "departure-tour-options";

/** A year or month radio group's options; `of` says which. */
export const DEPARTURE_CHOICES_TYPE = "departure-choices";
export const DEPARTURE_CHOICES_OF = ["year", "month"] as const;
export type DepartureChoicesOf = (typeof DEPARTURE_CHOICES_OF)[number];

/** The stored card a departure is drawn with (design.md D97). */
export const DEPARTURE_CARD_TEMPLATE_TYPE = "departure-card";
export const DEPARTURE_CARD_FIELD_TYPE = "departure-card-field";

/**
 * Which filter a stored control is, on the control's root. The bind finds a
 * control by this, never by its class, which the operator may change.
 */
export const DEPARTURE_FILTER_ATTR = "data-departure-filter";
export const DEPARTURE_FILTER_CONTROLS = [
  "form",
  "tour",
  "year",
  "month",
  "sort",
] as const;
export type DepartureFilterControl = (typeof DEPARTURE_FILTER_CONTROLS)[number];

/** One option of the tour picker, or of the year or month group. */
export interface DepartureChoice {
  value: string;
  label: string;
  chosen: boolean;
  /** A month's departures, or the year's for "every month". Years and
   *  tours have none. */
  count?: number;
}

/** One line the operator added in Tours → Configuration → Tour Page, with
 *  its icon already resolved to path data (`departureDetailLines`). */
export interface DepartureDetailLine {
  text: string;
  /** `d` values joined by `|`; `null` when the icon is cleared or unknown. */
  iconPath: string | null;
}

/**
 * What a departure's details borrow from the Tour Page settings, so a date
 * reads the same in the block and on the tour page. Each WhatsApp surface is
 * `null` unless `buildTourPageView` says it renders.
 */
export interface DepartureTourPageExtras {
  lines: DepartureDetailLine[];
  /** The icon button beside Book now. */
  whatsappButton: TourPageWhatsappView | null;
  /** The "Ask on WhatsApp" line at the foot of the details. */
  whatsappContact: TourPageWhatsappContactView | null;
}

export const NO_TOUR_PAGE_EXTRAS: DepartureTourPageExtras = {
  lines: [],
  whatsappButton: null,
  whatsappContact: null,
};

/** The "Ask on WhatsApp" line, in the operator's own words. */
export interface DepartureAsk {
  prompt: string;
  label: string;
  href: string;
}

/** One departure, as its stored card draws it. Built by the bind. */
export interface DepartureCardItem {
  row: DepartureRowDto;
  tour: DepartureTourDto;
  /** The block lists more than one tour, so the card names its tour. */
  showTour: boolean;
  /** "See more details" is on and has something to show. */
  details: boolean;
  /** The details start open: the first card, when the operator chose that. */
  open: boolean;
  /** The tour page with this date chosen; `null` for a sold-out date. */
  bookHref: string | null;
  /** The button's chat, naming the tour and the date; `null` when off. */
  whatsappHref: string | null;
  /** The operator's Tour Page detail lines. */
  lines: DepartureDetailLine[];
  /** The line at the foot of the details; `null` when off. */
  ask: DepartureAsk | null;
  /** The original price, struck through beside the price, with the saving;
   *  `null` when off, sold out, or the date has no reduction. */
  wasPrice: MoneyDto | null;
}

export const MONTH_SHORT_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * "Sat, 10 Oct 2026". A departure date is a calendar day, not an instant, so
 * it is read and written in UTC: a visitor's time zone never moves it.
 */
export const formatDepartureDay = (date: string): string =>
  new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));

/** The tour page with the date already chosen in its booking box (AC-18). */
export const departureBookPath = (
  tour: Pick<DepartureTourDto, "slug">,
  date: string,
): string => `${tourCardPath(tour)}?date=${encodeURIComponent(date)}`;

function setFilterParams(
  query: URLSearchParams,
  filters: DepartureFilters,
  tourFilter: boolean,
): void {
  if (tourFilter && filters.tours.length > 0) {
    query.set("tour", filters.tours.join(","));
  }
  if (filters.year !== null) query.set("year", String(filters.year));
  if (filters.month !== null) query.set("month", String(filters.month));
  if (filters.sort !== DEFAULT_DEPARTURE_SORT) query.set("sort", filters.sort);
}

/**
 * The `GET /v1/departures` query a block asks for a view, as URL parameters,
 * or `null` when the block can list nothing: set to hand-picked tours with
 * none picked. The storefront and the canvas both build it here, so the
 * editor shows what the published page will (AC-17).
 */
export function departuresQueryOf(
  settings: DepartureDatesSettings,
  filters: DepartureFilters,
  shown: number = DEPARTURES_PAGE_SIZE,
): URLSearchParams | null {
  if (settings.tours === "chosen" && settings.tourIds.length === 0) return null;
  const query = new URLSearchParams();
  if (settings.tours === "chosen")
    query.set("scope", settings.tourIds.join(","));
  if (settings.showFilter) setFilterParams(query, filters, settings.tourFilter);
  // No year toggle to choose with, so the next departures in any year.
  else query.set("allYears", "true");
  if (settings.hideSoldOut) query.set("hideSoldOut", "true");
  if (settings.max !== null) query.set("max", String(settings.max));
  query.set("perPage", String(shown));
  return query;
}
